import { Markdown } from '@/components/Markdown';
import styles from './control-view.module.css';
import { Card, CardTitle, CardHeader, CardContent } from '@/components/ui/card';
import {
  SlidesAdminConfig,
  SlidesAdminData,
  SlidesSubmissionData,
  SlidesViewConfig,
  SlidesViewData,
  useGetSubmissionsQuery,
} from '@/generated/graphql';
import {
  asTypedSubmissionQueryResult,
  TypedEngagement,
  TypedSubmission,
} from '@/utils/engagementTypeUtils';
import { useMemo } from 'react';
import { dataChanged } from '@/providers/AdminEngagementProvider/actions';
import { useAdminEngagementContext } from '@/providers/AdminEngagementProvider/AdminEngagementContext';
import { cn } from '@/lib/utils';

export function SlidesControlView() {
  const { dispatch, state } = useAdminEngagementContext();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { editorMode, ..._engagement } = state;
  const engagement = _engagement as TypedEngagement<
    SlidesAdminData,
    SlidesAdminConfig,
    SlidesViewData,
    SlidesViewConfig
  >;
  const { slidesDisplayOrder, currentSlide } = engagement.data;
  const { data: submissionsData } = useGetSubmissionsQuery({
    variables: { engagementId: engagement.id! },
    skip: !engagement.id,
  });

  const typedSubmissionsData =
    asTypedSubmissionQueryResult<SlidesSubmissionData>(
      submissionsData || { submissions: [] },
    );

  const lookup = useMemo(() => {
    if (!typedSubmissionsData.submissions) return null;
    return typedSubmissionsData.submissions.reduce(
      (acc, submission) => {
        acc[submission.id] = submission;
        return acc;
      },
      {} as Record<string, TypedSubmission<SlidesSubmissionData>>,
    );
  }, [typedSubmissionsData.submissions]);

  return (
    <div className="flex flex-col gap-4">
      {lookup ? (
        <div className="flex flex-col gap-4 p-4">
          {slidesDisplayOrder
            .map((slideId) => lookup[slideId])
            .filter(Boolean)
            .map((slide) => (
              <Slide
                key={slide.id}
                slide={slide}
                isCurrent={currentSlide === slide.id}
                onSetCurrent={() => {
                  console.log('setting current slide to', slide.id);
                  dispatch(
                    dataChanged({
                      data: { currentSlide: slide.id },
                    }),
                  );
                }}
              />
            ))}
        </div>
      ) : null}
    </div>
  );
}

function Slide({
  slide,
  isCurrent,
  onSetCurrent,
}: {
  slide: TypedSubmission<SlidesSubmissionData>;
  isCurrent: boolean;
  onSetCurrent: () => void;
}) {
  return (
    <Card
      className={isCurrent ? styles.currentSlide : ''}
      onClick={onSetCurrent}
    >
      <CardHeader>
        <CardTitle>{slide.data.title}</CardTitle>
      </CardHeader>
      <CardContent className="relative">
        <Markdown className={cn(styles.contentCard, 'relative')}>
          {slide.data.content}
        </Markdown>
        {slide.data.optionalImageUrl ? (
          <img
            src={slide.data.optionalImageUrl}
            alt={slide.data.title}
            className="absolute right-4 top-4 z-10 w-24 border-2 border-white object-cover"
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
